import React, { Fragment, FunctionComponent } from 'react';
import RecruitmentPhase, { OrderedRecruitmentPhases } from './RecruitmentPhase';
import { usePageBreakpoint } from 'app/components/layout/Breakpoint';
import { useI18n } from 'app/components/localization';
import Checkmark from 'app/components/svgs/Checkmark';
import { NumericBreakpoint } from 'app/constants/Breakpoints';
import { GREY_I, GREY_B, PURE_WHITE } from 'app/constants/Colors';
import { useClasses } from 'app/utils/css';

const CIRCLE_HEIGHT = '2.5';  // In em.
const STEP_COUNT_COLOR = PURE_WHITE;
const ACTIVE_COLOR = GREY_B;
const INACTIVE_COLOR = GREY_I;

const ProgressBar = ({ recruitmentPhase }: { recruitmentPhase: RecruitmentPhase }) => {
  const isWide = usePageBreakpoint() >= NumericBreakpoint.Wide;
  const classes = useClasses({
    wrapper: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      padding: '1em 0',
      fontSize: isWide ? '12px' : '8px',
    },
  });
  return (
    <div className={classes.wrapper}>
      {OrderedRecruitmentPhases.map((phase, i) => (
        <ProgressBlock
          active={phase <= recruitmentPhase}
          key={i}
          first={i === 0}
          recruitmentPhase={phase}
        />
      ))}
    </div>
  );
};

const StepNumber: FunctionComponent<{ recruitmentPhase: RecruitmentPhase }> = ({ recruitmentPhase }) => {
  if (recruitmentPhase === RecruitmentPhase.Done) {
    return (<Checkmark style={{ height: '1em', width: '1em', fill: STEP_COUNT_COLOR }} />);
  } else {
    return (<span>{recruitmentPhase}</span>);
  }
};

const StepName: FunctionComponent<{ recruitmentPhase: RecruitmentPhase }> = ({ recruitmentPhase }) => {
  const { t } = useI18n();
  switch (recruitmentPhase) {
    case RecruitmentPhase.Signup:
      return <>{t('page.teach.progress.signup')}</>;

    case RecruitmentPhase.VideoTest:
      return <>{t('page.teach.progress.video-test')}</>;

    case RecruitmentPhase.LanguageTest:
      return <>{t('page.teach.progress.language-test')}</>;

    case RecruitmentPhase.Done:
      return <>{t('page.teach.progress.done')}</>;

    default:
      throw new Error(`Recruitment phase, ${recruitmentPhase}, is not handled.`);
  }
};

const ProgressBlock = ({
  active = false,
  first = false,
  recruitmentPhase,
}: {
  active: boolean;
  first: boolean;
  recruitmentPhase: RecruitmentPhase;
}) => {
  const classes = useClasses({
    bar: {
      flexGrow: 1,
      height: '2px',
      margin: '0 0.6em',
      backgroundColor: active ? ACTIVE_COLOR : INACTIVE_COLOR,
    },

    stepName: {
      color: active ? ACTIVE_COLOR : INACTIVE_COLOR,
    },

    stepNumber: {
      borderRadius: '50%',
      color: STEP_COUNT_COLOR,
      backgroundColor: active ? ACTIVE_COLOR : INACTIVE_COLOR,
      lineHeight: CIRCLE_HEIGHT,
      marginRight: '0.6em',
      height: `${CIRCLE_HEIGHT}em`,
      width: `${CIRCLE_HEIGHT}em`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  });

  return (
    <Fragment>
      {!first && <div className={classes.bar} />}
      <div className={classes.stepNumber}>
        <StepNumber recruitmentPhase={recruitmentPhase} />
      </div>
      <div className={classes.stepName}>
        <StepName recruitmentPhase={recruitmentPhase} />
      </div>
    </Fragment>
  );
};

export default ProgressBar;
